import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Beautiful Alps image with day-to-night transition
const themes = {
  v1: {
    name: "Golden Peaks",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2560&q=80",
    animation: "animate-slow-zoom",
    hasClouds: true,
  },
};

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) throw new Error("Authentication failed");

        const user = await response.json();
        toast.success(`Welcome, ${user.name || user.email}`);
        navigate("/", { replace: true, state: { user } });
      } catch (error) {
        toast.error("Authentication failed");
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white text-base font-inter tracking-[0.3em] uppercase animate-pulse">
        Authenticating...
      </div>
    </div>
  );
};

// Landing Page Component
const LandingPage = () => {
  const [activeTheme, setActiveTheme] = useState("v1");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [magicToken, setMagicToken] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  const theme = themes[activeTheme];

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
    }
  }, [location.state]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {}
    };
    if (!location.state?.user) checkAuth();
  }, [location.state]);

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailRequest = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/email/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to send magic link");
      if (data.demo_token) setMagicToken(data.demo_token);
      setStep("verify");
      toast.success("Check your email for the magic link");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyToken = async () => {
    if (!magicToken) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/email/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token: magicToken }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Invalid token");
      setUser(data);
      setEmailModalOpen(false);
      setStep("email");
      setEmail("");
      setMagicToken("");
      toast.success(`Welcome, ${data.name || data.email}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {}
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-black">
      {/* Background Images with animation */}
      {Object.entries(themes).map(([key, t]) => (
        <div
          key={key}
          className={`absolute inset-0 transition-opacity duration-1000 ${activeTheme === key ? 'opacity-100' : 'opacity-0'}`}
        >
          <div
            className={`absolute inset-0 ${t.animation}`}
            style={{
              backgroundImage: `url(${t.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          {/* Day-to-night transition overlay for V2 */}
          {t.hasClouds && activeTheme === key && (
            <>
              <div className="day-night-overlay" />
              <div className="stars-overlay" />
              <div className="shooting-star shooting-star-1" />
              <div className="shooting-star shooting-star-2" />
              <div className="cloud-layer cloud-layer-1" />
              <div className="cloud-layer cloud-layer-2" />
            </>
          )}
        </div>
      ))}

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center px-6 pt-20 md:pt-28 lg:pt-32">
        {/* Logo with Alps Icon */}
        <div className="text-center flex flex-col items-center">
          {/* Mobile: stacked layout, Desktop: horizontal layout */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 md:gap-6">
            {/* Alps Peaks Icon */}
            <svg 
              className="w-12 h-10 sm:w-10 sm:h-8 md:w-14 md:h-10 lg:w-16 lg:h-12 sm:-mt-6 md:-mt-8" 
              viewBox="0 0 60 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline 
                points="5,35 18,12 28,22 40,2 55,35" 
                stroke="white" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            {/* Text */}
            <div className="text-center sm:text-left">
              <h1
                className="font-inter text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-extralight tracking-[0.25em] sm:tracking-[0.3em] uppercase text-white"
                style={{ fontWeight: 200 }}
                data-testid="brand-name"
              >
                Montchatel Capital
              </h1>
              <p
                className="font-inter text-xs sm:text-sm md:text-base italic font-extralight tracking-[0.12em] mt-1 md:mt-2 text-white/60"
                data-testid="tagline"
              >
                For The Few
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1" />

        {/* Auth Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xl justify-center mb-32">
          {user ? (
            <div className="flex flex-col items-center gap-6 w-full">
              <p className="font-inter text-sm tracking-[0.2em] uppercase text-white/70">
                Welcome, {user.name || user.email}
              </p>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="px-10 py-4 border border-white/40 bg-white/10 hover:bg-white/20 text-white font-inter text-xs tracking-[0.15em] uppercase transition-all duration-300 backdrop-blur-sm"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={handleGoogleSignIn}
                data-testid="google-signin-btn"
                className="flex items-center justify-center gap-3 px-8 py-4 border border-white/40 bg-white/10 hover:bg-white/20 text-white font-inter text-xs tracking-[0.15em] uppercase transition-all duration-300 backdrop-blur-sm min-w-[220px]"
              >
                <FcGoogle className="w-5 h-5" />
                <span>Sign in with Google</span>
              </button>
              <button
                onClick={() => setEmailModalOpen(true)}
                data-testid="email-signin-btn"
                className="flex items-center justify-center gap-3 px-8 py-4 border border-white/40 bg-white/10 hover:bg-white/20 text-white font-inter text-xs tracking-[0.15em] uppercase transition-all duration-300 backdrop-blur-sm min-w-[220px]"
              >
                <Mail className="w-5 h-5" />
                <span>Sign in with Email</span>
              </button>
            </>
          )}
        </div>

        {/* Geneva Footer */}
        <div className="pb-10 text-center">
          <p className="font-inter text-xs tracking-[0.25em] uppercase text-white/50" data-testid="footer-location">
            Geneva
          </p>
        </div>
      </div>

      {/* Version Switcher - hidden since only one theme */}
      {Object.keys(themes).length > 1 && (
        <div className="fixed bottom-20 right-8 z-50 flex items-center gap-3 px-4 py-2 rounded-full backdrop-blur-md bg-black/30" data-testid="version-switcher">
          {Object.entries(themes).map(([key, t]) => (
            <button
              key={key}
              onClick={() => setActiveTheme(key)}
              data-testid={`theme-${key}-btn`}
              aria-label={t.name}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${activeTheme === key ? "bg-white" : "bg-white/40"} hover:scale-150`}
            />
          ))}
        </div>
      )}

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md" aria-describedby="email-signin-description">
          <DialogHeader>
            <DialogTitle className="font-inter text-xl tracking-[0.1em] uppercase text-center font-light">
              {step === "email" ? "Sign In" : "Verify Email"}
            </DialogTitle>
            <p id="email-signin-description" className="sr-only">
              {step === "email" ? "Enter your email" : "Enter verification code"}
            </p>
          </DialogHeader>
          <div className="mt-6 space-y-4">
            {step === "email" ? (
              <>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500 h-12 font-inter"
                />
                <Button
                  onClick={handleEmailRequest}
                  disabled={loading || !email}
                  data-testid="send-magic-link-btn"
                  className="w-full h-12 bg-white text-black hover:bg-neutral-200 font-inter text-xs tracking-[0.15em] uppercase"
                >
                  {loading ? "Sending..." : "Send Magic Link"}
                </Button>
              </>
            ) : (
              <>
                <p className="text-neutral-400 text-sm text-center font-inter">Enter the verification code</p>
                <Input
                  type="text"
                  placeholder="Enter verification token"
                  value={magicToken}
                  onChange={(e) => setMagicToken(e.target.value)}
                  data-testid="magic-token-input"
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500 h-12 font-inter"
                />
                <Button
                  onClick={handleVerifyToken}
                  disabled={loading || !magicToken}
                  data-testid="verify-token-btn"
                  className="w-full h-12 bg-white text-black hover:bg-neutral-200 font-inter text-xs tracking-[0.15em] uppercase"
                >
                  {loading ? "Verifying..." : "Verify"}
                </Button>
                <button onClick={() => setStep("email")} className="w-full text-neutral-500 hover:text-white text-sm font-inter">
                  Back
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
